<template>
  <component :is="modal ? 'modal' : 'div'" :label="triggerLabel">
    <div class="provider-list warm-gray">
      <list-filter
        v-if="activeScreen === FILTER"
        v-model="tags"
        :filtered-hospitals="filteredHospitals"
        :filters="filters"
        @filterClose="onFilterClose"
        @filterReset="onFilterReset"
      />

      <detail
        v-if="activeScreen === DETAIL"
        :detail-item="detailItem"
        @listClick="onListClick"
      />

      <div v-if="activeScreen === LIST">
        <h2 class="provider-list__title">Hospital Day Comfort</h2>
        <form class="sanitasForms provider-list__form" @submit.prevent>
          <div class="provider-list__input-container">
            <text-input
              v-model="inputName"
              :title-label="$t('inputHospitalName')"
              autofocus
              name="hospitalName"
              :max-length="100"
              class="provider-list__input-name"
              autocomplete="off"
            >
              <template #after>
                <div class="provider-list__input-subtext">
                  {{ $t('inputHospitalSubtext') }}
                </div>
              </template>
            </text-input>
            <text-input
              v-model="inputZipOrTown"
              :title-label="$t('inputZipOrTown')"
              :max-length="100"
              name="inputZipOrTown"
              class="provider-list__input-zip"
              autocomplete="off"
            ></text-input>
            <button
              type="button"
              class="provider-list__input-filter-open"
              @click="filterOpen"
            >
              <dotcom-icon name="filter" art="light" />
              {{ textFilterButton }}
            </button>
          </div>

          <div
            v-if="activeTags.length"
            class="provider-list__filter-active-tag-container"
          >
            <div v-for="tagKey in activeTags" :key="tagKey">
              <button
                type="button"
                class="provider-list__filter-active-tag"
                @click="tagsResetOnlyThis(tagKey)"
              >
                {{ tagNames[tagKey] }}&emsp;<dotcom-icon
                  name="xmark"
                  art="light"
                />
              </button>
            </div>
            <button
              v-if="activeTags.length"
              type="button"
              class="provider-list__filter-active-tag-button-reset"
              @click="onFilterReset"
            >
              {{ $t('filterReset') }}
            </button>
          </div>

          <div class="provider-list__content">
            <div
              v-for="(hospital, hospitalIndex) in filteredHospitals"
              :key="hospitalIndex"
            >
              <item :hospital="hospital" @detailClick="onDetailClick"></item>
            </div>
            <div
              v-if="initDone && !filteredHospitals.length"
              class="provider-list__no-result"
            >
              <h5 class="provider-list__no-result-title">
                {{ $t('listNoResult') }}
              </h5>
              <p class="provider-list__no-result-subtext">
                {{ $t('listNoResultAdaptSearch') }}
              </p>
              <button
                type="button"
                class="provider-list__no-result-button-clear"
                @click="clearSearch"
              >
                {{ $t('listNoResultResetSearch') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </component>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import TextInput from '@/components/sanitas-components/TextInput'
import { client } from '@/helpers/http'
import DotcomIcon from '@/modules/icon/Icon'

import Detail from './components/Detail'
import Item from './components/Item'
import ListFilter from './components/ListFilter'
import Modal from './components/Modal'

const LIST = 1
const DETAIL = 2
const FILTER = 3

export default {
  name: 'ProviderList',
  components: {
    Modal,
    ListFilter,
    Detail,
    Item,
    DotcomIcon,
    TextInput,
  },

  props: {
    providerType: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
    },
    webview: {
      type: Boolean,
    },
    triggerLabel: {
      type: String,
      default: '',
    },
  },

  setup() {
    const language = window.Sanitas.language || 'de'
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
      language,
    }
  },
  data() {
    return {
      initDone: false,
      LIST,
      DETAIL,
      FILTER,
      activeScreen: LIST,
      inputName: '',
      inputZipOrTown: '',
      detailItem: null,
      tags: {},
      tagNames: [],
      filters: [],
      hospitals: [],
      modalContainer: null,
      prevScrollValue: 0,
      clickType: '',
    }
  },
  computed: {
    baseDomain() {
      return process.env.VUE_APP_ROOT_API
    },
    backendUrl() {
      return `${this.baseDomain}/public/masterdata/v1/hospital-stay/entities?type=${this.providerType}&language=${this.language}`
    },
    textFilterButton() {
      return this.activeTags.length
        ? this.$t('filterModify')
        : this.$t('filter')
    },
    activeTags() {
      return Object.entries(this.tags)
        .filter(item => item[1])
        .map(item => item[0])
    },
    filteredHospitals() {
      if (!this.initDone) return []

      const extractedZip = this.inputZipOrTown.replaceAll(/(\D|\s)/g, '')
      const extractedTown = this.inputZipOrTown.replaceAll(/(\d)/g, '').trim()

      return this.hospitals
        .filter(hospital => {
          return (
            hospital.name.toLowerCase().indexOf(this.inputName.toLowerCase()) >
            -1
          )
        })
        .filter(hospital => {
          return (
            extractedZip.length === 0 ||
            hospital.address.zipCode.substring(0, extractedZip.length) ===
              extractedZip
          )
        })
        .filter(hospital => {
          return (
            extractedTown.length === 0 ||
            hospital.address.town
              .toLowerCase()
              .indexOf(extractedTown.toLowerCase()) > -1
          )
        })
        .filter(hospital => {
          return (
            this.activeTags.length === 0 ||
            this.activeTags.every(activeTag => {
              return hospital.filters.some(group => {
                return group.tags.some(tag => {
                  return activeTag === tag.tagKey
                })
              })
            })
          )
        })
    },
  },
  mounted() {
    this.loadList()
  },
  updated() {
    this.positionTop()
  },

  methods: {
    prepareData() {
      let tagArray = this.filters.flatMap(group =>
        group.tags.map(tag => tag.tagKey),
      )
      this.tags = tagArray.reduce((o, key) => ({ ...o, [key]: false }), {})

      let tagArrayNames = this.filters.flatMap(group =>
        group.tags.map(tag => ({ ...tag })),
      )
      this.tagNames = tagArrayNames.reduce(
        (o, tag) => ({ ...o, [tag.tagKey]: tag.tagName }),
        {},
      )

      this.sortHospitalsByName()
      this.initDone = true
    },
    sortHospitalsByName() {
      this.hospitals.sort(compareName)

      function compareName(a, b) {
        const nameA = a.name.toUpperCase()
        const nameB = b.name.toUpperCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      }
    },
    setPrevScrollValue() {
      this.modalContainer = document.querySelector('.modal__container')
      this.prevScrollValue = !this.modalContainer
        ? window.scrollY
        : this.modalContainer.scrollTop
    },
    onDetailClick(item) {
      this.setPrevScrollValue()
      this.detailItem = item
      this.activeScreen = DETAIL
      this.clickType = 'DETAIL'
    },
    onListClick() {
      this.detailItem = null
      this.activeScreen = LIST
      this.clickType = 'LIST'
    },
    filterOpen() {
      this.setPrevScrollValue()
      this.activeScreen = FILTER
      this.clickType = 'FILTER_OPEN'
    },
    onFilterClose() {
      this.activeScreen = LIST
      this.clickType = 'FILTER_CLOSE'
    },
    tagsResetOnlyThis(paraTag) {
      this.tags[paraTag] = false
    },
    onFilterReset() {
      Object.keys(this.tags).forEach(key => {
        this.tags[key] = false
      })
    },
    positionTop() {
      if (this.clickType === 'DETAIL' || this.clickType === 'FILTER_OPEN') {
        if (this.webview) {
          window.scrollTo(0, 0)
        } else {
          this.modalContainer.scrollTop = 0
        }
      }

      if (this.clickType === 'LIST' || this.clickType === 'FILTER_CLOSE') {
        if (this.webview) {
          window.scrollTo(0, this.prevScrollValue)
        } else {
          this.modalContainer.scrollTop = this.prevScrollValue
        }
      }
    },
    clearSearch() {
      this.inputName = ''
      this.inputZipOrTown = ''
    },
    loadList() {
      client()
        .get(this.backendUrl)
        .then(({ data }) => {
          if (!data?.data) {
            console.log('error no data')
            return
          }
          this.hospitals = data.data.entities
          this.filters = data.data.filters
          this.prepareData()
        })
        .catch(error => {
          console.log(error)
        })
    },
  },
}
</script>

<style lang="postcss">
.provider-list {
  background-color: var(--c-primary-neutral-2);
  padding: var(--spacing-8);
  max-width: 940px;
  min-height: 100%;

  .provider-list__form {
    .provider-list__input-container {
      .text-input-container {
        margin-bottom: var(--spacing-2);
        .text-input {
          .input {
            font-size: 16px;
            padding: 20px 10px 2px 10px;
          }
        }
      }
    }
  }
}

.provider-list__content {
  overflow: auto;
}

.provider-list__title {
  font-size: 20px;
  margin-bottom: var(--spacing-4);
}

.provider-list__input-container {
  display: flex;
  justify-content: stretch;
  gap: 0 var(--spacing-4);
  flex-direction: column;
  flex-wrap: wrap;
  margin-bottom: var(--spacing-6);
}

.provider-list__input-filter-open {
  background: none;
  border: none;
  padding: 0 0 0 10px;
  color: var(--c-primary-color-3);
  cursor: pointer;
  text-align: left;
  font-weight: bold;
  font-size: 14px;
}

.provider-list__input-subtext {
  color: var(--c-secondary-neutral-2);
  font-size: 14px;
  padding-left: 10px;
  margin-top: var(--spacing-2);
}

.provider-list__input-name,
.provider-list__input-zip {
  flex: 1;
}

.provider-list__input-zip {
  margin-bottom: var(--spacing-4);
}

.provider-list__filter-active-tag {
  background: none;
  border: none;
  cursor: pointer;
  background-color: var(--c-primary-color-1);
  border-radius: 18px;
  color: var(--c-primary-neutral-3);
  font-size: 12px;
  font-weight: bold;
  padding: var(--spacing-2) var(--spacing-4);

  &:hover {
    cursor: pointer;
    background-color: var(--c-primary-color-3-a);
  }
}

.provider-list__filter-active-tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-2) var(--spacing-4);
  margin-bottom: var(--spacing-4);
}

.provider-list__filter-active-tag-button-reset {
  background: none;
  border: none;
  padding: var(--spacing-2);
  color: var(--c-primary-color-3);
  cursor: pointer;
  text-align: left;
  font-weight: bold;
  font-size: 14px;
}

.provider-list__no-result {
  background-color: var(--c-tint-color-6);
  padding: var(--spacing-4);
}

.provider-list__no-result-title {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: var(--spacing-2);
}

.provider-list__no-result-subtext {
  font-size: 14px;
  margin-bottom: var(--spacing-2);
}

.provider-list__no-result-button-clear {
  background: none;
  border: none;
  padding: 0;
  color: var(--c-primary-color-3);
  cursor: pointer;
  text-align: left;
  font-weight: bold;
  font-size: 14px;
}

@media (--sm) {
  .provider-list__input-container {
    flex-direction: row;
  }

  .provider-list__input-filter-open {
    font-size: 16px;
  }

  .provider-list__filter-active-tag {
    font-size: 14px;
  }
}
</style>
